import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { SearchDropdown } from '@/components/SearchDropdown';

export interface SessionsFilterState {
  search: string;
  qualificationStatus: string[];
  bookedStatus: string[];
  sessionStatus: string[];
  sourceEnvironment: string[];
  dateRange?: DateRange;
}

interface SessionsFiltersProps {
  filters: SessionsFilterState;
  onFiltersChange: (patch: Partial<SessionsFilterState>) => void;
  onReset: () => void;
}

const sessionStatusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

const bookedStatusOptions = [
  { label: 'Booked', value: 'booked' },
  { label: 'Not Booked', value: 'not_booked' },
];

const qualificationStatusOptions = [
  { label: 'Qualified', value: 'qualified' },
  { label: 'Unqualified', value: 'unqualified' },
  { label: 'Unknown', value: 'unknown' },
];

const environmentOptions = [
  { label: 'Production', value: 'production' },
  { label: 'Staging', value: 'staging' },
];

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { label: string; value: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${label} (${selected.length})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <span className="truncate text-left">{triggerLabel}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        <div className="flex flex-col gap-1">
          {options.map(({ label: optLabel, value }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              {optLabel}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const SessionsFilters: React.FC<SessionsFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  const handleDateChange = (range: DateRange | undefined) => {
    onFiltersChange({ dateRange: range });
  };

  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.qualificationStatus.length > 0 ||
    filters.bookedStatus.length > 0 ||
    filters.sessionStatus.length > 0 ||
    !(filters.sourceEnvironment.length === 1 && filters.sourceEnvironment[0] === 'production') ||
    !!filters.dateRange;

  return (
    <div className="flex w-full items-center gap-2 overflow-x-auto">

      <MultiSelectFilter
        label="Environments"
        options={environmentOptions}
        selected={filters.sourceEnvironment}
        onChange={(values) => onFiltersChange({ sourceEnvironment: values })}
      />

      <MultiSelectFilter
        label="Sessions"
        options={sessionStatusOptions}
        selected={filters.sessionStatus}
        onChange={(values) => onFiltersChange({ sessionStatus: values })}
      />

      <MultiSelectFilter
        label="Outcomes"
        options={bookedStatusOptions}
        selected={filters.bookedStatus}
        onChange={(values) => onFiltersChange({ bookedStatus: values })}
      />

      <MultiSelectFilter
        label="Qualifications"
        options={qualificationStatusOptions}
        selected={filters.qualificationStatus}
        onChange={(values) => onFiltersChange({ qualificationStatus: values })}
      />

      <div className="shrink-0">
        <SearchDropdown
          value={filters.search}
          onChange={(value) => onFiltersChange({ search: value })}
          placeholder="Search sessions..."
          buttonClassName="h-8 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          iconClassName="text-icon"
        />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-8 rounded-md border border-border bg-background px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors',
                !filters.dateRange && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-3.5 w-3.5" />
              {filters.dateRange?.from ? (
                filters.dateRange.to ? (
                  <>
                    {format(filters.dateRange.from, 'MMM dd')} - {format(filters.dateRange.to, 'MMM dd, yyyy')}
                  </>
                ) : (
                  format(filters.dateRange.from, 'MMM dd, yyyy')
                )
              ) : (
                'Date range'
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={filters.dateRange?.from}
              selected={filters.dateRange}
              onSelect={handleDateChange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground hover:text-heading" onClick={onReset}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default SessionsFilters;
